import React, { useEffect, useRef, useState } from "react";
import { Upload, Sparkles, Trash2, Loader2 } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { toast } from "sonner";

const MAX = 10;

// Read file as base64 (without data: prefix) for AI vision input
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const str = reader.result;
      const idx = str.indexOf(",");
      resolve(str.slice(idx + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function ImageUploader({ images, setImages, onAiSuggest, testId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [analysing, setAnalysing] = useState(false);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (images.length + arr.length > MAX) {
      toast.error(`Max ${MAX} photos`);
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of arr) {
        if (!/image\/(jpeg|png|webp)/.test(f.type)) {
          toast.error(`${f.name}: only JPEG/PNG/WEBP`);
          continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`${f.name}: max 10MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ path: r.data.path, url: imageUrl(r.data.path) });
      }
      const merged = [...images, ...uploaded];
      setImages(merged);
      // Trigger AI suggest off the first image only the first time
      if (onAiSuggest && uploaded.length && merged.length >= 1 && !images.length) {
        try {
          setAnalysing(true);
          const b64 = await fileToBase64(arr[0]);
          const r = await api.post("/ai/analyze-image", { image_base64: b64, mime_type: arr[0].type });
          if (r.data?.nsfw) {
            toast.error("Image flagged as inappropriate and won't be used for AI suggestions.");
          } else if (r.data?.is_apple_part === false) {
            toast("Image doesn't look like an Apple part — please double-check.", { icon: "⚠️" });
            onAiSuggest(r.data);
          } else {
            onAiSuggest(r.data);
            toast.success("AI auto-filled likely fields — review before submitting");
          }
        } catch (err) {
          // soft fail
        } finally {
          setAnalysing(false);
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => setImages(images.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {images.map((img, i) => (
          <div key={img.path} className="relative w-24 h-24 md:w-28 md:h-28 rounded-xl overflow-hidden border border-[#d2d2d7]">
            <img src={img.url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 grid place-items-center hover:bg-black/80"
              aria-label="Remove image"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
            {i === 0 && (
              <span className="absolute bottom-1 left-1 bg-white/90 text-[10px] font-medium px-1.5 py-0.5 rounded">Main</span>
            )}
          </div>
        ))}
        {images.length < MAX && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="w-24 h-24 md:w-28 md:h-28 rounded-xl border-2 border-dashed border-[#d2d2d7] hover:border-[#0071e3] hover:bg-[#0071e3]/5 grid place-items-center text-[#86868b] hover:text-[#0071e3] transition-colors"
          >
            {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div className="text-xs text-[#86868b] flex items-center gap-2 flex-wrap">
        <span>Minimum 2 photos. JPEG / PNG / WEBP up to 10MB each.</span>
        {analysing && (
          <span className="inline-flex items-center gap-1 text-[#0071e3]">
            <Sparkles className="w-3.5 h-3.5" /> Analysing with AI…
          </span>
        )}
      </div>
    </div>
  );
}
